import { PageLayout } from "../../shared/layouts/PageLayout";
import { useParams } from "react-router-dom";
import { adminDashboardBreadcrumbs } from "@features/AdminDashboard/admin-dashboard-breadcrumbs";
import { useGetUser } from "@queries/users/useGetUser";
import { useAdminGetUserSubscription } from "@queries/subscription/useAdminGetUserSubscription";
import { useAdminGetUserThreads } from "@queries/chat-logs/useAdminGetUserThreads";
import { Stack } from "@mui/material";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { formatInternationalPhoneNumber } from "@shared/utils/form-converters";
import { AdminResetUserPassword } from "@features/Users/AdminResetUserPassword";
import { AdminImpersonateUser } from "@features/Users/AdminImpersonateUser";
import { isSsoOnlyUser } from "@features/Users/users-helpers";
import { capitalize, humanizeEnumValue } from "@shared/utils/text-formatter";
import { formatToCurrency } from "@shared/utils/formatters";
import { StatusPill } from "@shared/components/StatusPill";
import { SubscriptionStatusDetailsPaymentStatusEnum } from "@shared/apis/sidekick/generated";
import { ISubscriptionInfo } from "@features/Subscription/ManageSubscription/mange-subscription-models";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@avenue-8/sidekick-ui";

type StatusTone = "success" | "warning" | "error";

const PAYMENT_STATUS_TONE: Record<
  SubscriptionStatusDetailsPaymentStatusEnum,
  StatusTone
> = {
  UpToDate: "success",
  Trialing: "success",
  Pending: "warning",
  Paused: "warning",
  Overdue: "error",
  Cancelled: "error",
};

const StyledCardContent = styled(CardContent)`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const InfoLabel = styled(Typography)`
  font-weight: 500;
  width: 180px;
  color: ${(p) => p.theme.text.colors.secondary};
`;

const InfoValue = styled.div`
  flex: 1;
`;

const ThreadsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ThreadItem = styled(Link)`
  display: block;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  background-color: ${(p) => p.theme.colors.background2};
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const getToneColors = (tone: StatusTone, theme: ReturnType<typeof useTheme>) => {
  switch (tone) {
    case "success":
      return {
        color: theme.colors.success,
        backgroundColor: `${theme.colors.success}33`,
      };
    case "warning":
      return {
        color: theme.colors.warning,
        backgroundColor: `${theme.colors.warning}33`,
      };
    case "error":
      return {
        color: theme.colors.error,
        backgroundColor: `${theme.colors.error}33`,
      };
  }
};

const formatSubscriptionType = (type: ISubscriptionInfo["type"]) => {
  switch (type) {
    case "paid":
      return "Paid Subscription";
    case "free":
      return "Free Subscription";
    default:
      return type;
  }
};

export function UserProfilePage() {
  const theme = useTheme();
  const { userId = "" } = useParams<{
    userId: string;
  }>();

  const userQuery = useGetUser({
    id: userId,
  });

  const userSubscriptionQuery = useAdminGetUserSubscription({
    userId,
  });

  const userThreadsQuery = useAdminGetUserThreads({
    userId,
  });

  const user = userQuery.data;
  const subscription = userSubscriptionQuery.data;
  const userThreads =
    userThreadsQuery.data?.pages.map((page) => page.threads).flat() ?? [];

  const breadcrumbs = {
    current: {
      label: user?.name || user?.email || "",
    },
    previous: [
      adminDashboardBreadcrumbs.dashboard,
      adminDashboardBreadcrumbs.users,
    ],
  };

  const isLoading =
    userQuery.isLoading ||
    userSubscriptionQuery.isLoading ||
    userThreadsQuery.isLoading;

  if (isLoading) {
    return (
      <PageLayout breadcrumbs={breadcrumbs}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!user) {
    return (
      <PageLayout breadcrumbs={breadcrumbs}>
        <Typography variant="h5" color="error">
          User not found
        </Typography>
      </PageLayout>
    );
  }

  return (
    <PageLayout breadcrumbs={breadcrumbs}>
      <Grid container spacing={3}>
        <Grid
          item
          xs={12}
          md={8}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Card>
            <StyledCardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider />

              <InfoRow>
                <InfoLabel>Name</InfoLabel>
                <InfoValue>{user.name || "—"}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{user.email}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  <StatusPill
                    label={user.status}
                    {...getToneColors(
                      user.enabled ? "success" : "error",
                      theme,
                    )}
                  />
                </InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Email Confirmed</InfoLabel>
                <InfoValue>{user.emailConfirmed ? "Yes" : "No"}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Brand</InfoLabel>
                <InfoValue>
                  {user.brand ? (
                    <Typography color="primary" fontWeight={600}>
                      {capitalize(user.brand)}
                    </Typography>
                  ) : (
                    "—"
                  )}
                </InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Created On</InfoLabel>
                <InfoValue>
                  {user.createdOn
                    ? format(new Date(user.createdOn), "MMM d, yyyy h:mm a")
                    : "—"}
                </InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Timezone</InfoLabel>
                <InfoValue>{user.timezone || "—"}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>State</InfoLabel>
                <InfoValue>{user.countryState || "—"}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Phone Number</InfoLabel>
                <InfoValue>
                  {formatInternationalPhoneNumber(user.phoneNumber) || "—"}
                </InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>MLS ID</InfoLabel>
                <InfoValue>{user.mlsId || "—"}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Office Name</InfoLabel>
                <InfoValue>{user.officeName || "—"}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Enabled MLS providers</InfoLabel>
                <InfoValue>
                  {user.enabledMlsProviders?.join(", ") ||
                    `All available from plan: "${user.subscriptionPlanId}"`}
                </InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Tracking ID</InfoLabel>
                <InfoValue>{user.trackingId}</InfoValue>
              </InfoRow>
            </StyledCardContent>
          </Card>

          {subscription && (
            <Card>
              <StyledCardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Details
                </Typography>
                <Divider />

                <InfoRow>
                  <InfoLabel>Type</InfoLabel>
                  <InfoValue>{formatSubscriptionType(subscription.type)}</InfoValue>
                </InfoRow>

                {subscription.details && (
                  <>
                    <InfoRow>
                      <InfoLabel>Payment Status</InfoLabel>
                      <InfoValue>
                        <StatusPill
                          label={humanizeEnumValue(
                            subscription.details.paymentStatus,
                          )}
                          {...getToneColors(
                            PAYMENT_STATUS_TONE[
                              subscription.details.paymentStatus
                            ],
                            theme,
                          )}
                        />
                      </InfoValue>
                    </InfoRow>

                    <InfoRow>
                      <InfoLabel>Payment Method</InfoLabel>
                      <InfoValue>
                        {subscription.details.paymentMethod?.brand
                          ? `${subscription.details.paymentMethod.brand} ending in ${subscription.details.paymentMethod.last4}`
                          : subscription.details.otherPaymentMethod?.type ||
                            "—"}
                      </InfoValue>
                    </InfoRow>

                    <InfoRow>
                      <InfoLabel>Next Payment</InfoLabel>
                      <InfoValue>
                        {format(
                          new Date(subscription.details.nextPaymentDate),
                          "MMM d, yyyy",
                        )}
                        {` (${formatToCurrency(subscription.details.nextPaymentAmount / 100)})`}
                      </InfoValue>
                    </InfoRow>

                    {subscription.details.lastPaymentDate && (
                      <InfoRow>
                        <InfoLabel>Last Payment</InfoLabel>
                        <InfoValue>
                          {format(
                            new Date(subscription.details.lastPaymentDate),
                            "MMM d, yyyy",
                          )}
                        </InfoValue>
                      </InfoRow>
                    )}

                    {subscription.details.trialEndDate && (
                      <InfoRow>
                        <InfoLabel>Trial End</InfoLabel>
                        <InfoValue>
                          {format(
                            new Date(subscription.details.trialEndDate),
                            "MMM d, yyyy",
                          )}
                        </InfoValue>
                      </InfoRow>
                    )}

                    {subscription.details.cancellationDate && (
                      <InfoRow>
                        <InfoLabel>Cancelled On</InfoLabel>
                        <InfoValue>
                          {format(
                            new Date(subscription.details.cancellationDate),
                            "MMM d, yyyy",
                          )}
                        </InfoValue>
                      </InfoRow>
                    )}

                    {subscription.details.scheduledCancellationDate && (
                      <InfoRow>
                        <InfoLabel>Cancels On</InfoLabel>
                        <InfoValue>
                          {format(
                            new Date(
                              subscription.details.scheduledCancellationDate,
                            ),
                            "MMM d, yyyy",
                          )}
                        </InfoValue>
                      </InfoRow>
                    )}
                  </>
                )}
              </StyledCardContent>
            </Card>
          )}
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Card>
            <StyledCardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Divider />

              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/users/${userId}/edit`}
                  fullWidth
                >
                  Edit User
                </Button>

                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/users/${userId}/subscription`}
                  fullWidth
                >
                  Manage Subscription
                </Button>

                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/users/${userId}/thread-logs`}
                  fullWidth
                >
                  View Thread Logs
                </Button>

                <AdminResetUserPassword
                  isLoading={userQuery.isLoading}
                  userEmail={user.email}
                  fullWidth
                  disabled={isSsoOnlyUser(user)}
                  disabledTooltip="This user signed up via SSO and can't reset a password."
                />

                <AdminImpersonateUser
                  isLoading={userQuery.isLoading}
                  userName={user.name}
                  userEmail={user.email}
                  fullWidth
                />
              </Stack>
            </StyledCardContent>
          </Card>

          {userThreads.length > 0 && (
            <Card>
              <StyledCardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Threads
                </Typography>
                <Divider />

                <ThreadsContainer>
                  {userThreads.slice(0, 5).map((thread) => (
                    <ThreadItem
                      key={thread.id}
                      to={`/admin/users/${userId}/thread-logs/${thread.id}`}
                    >
                      <Typography variant="subtitle1" noWrap>
                        {thread.title || "Untitled Thread"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {thread.createdAt
                          ? format(new Date(thread.createdAt), "MMM d, yyyy")
                          : "—"}
                      </Typography>
                    </ThreadItem>
                  ))}

                  {userThreads.length > 5 && (
                    <Box textAlign="center" mt={2}>
                      <Button
                        component={Link}
                        to={`/admin/users/${userId}/thread-logs`}
                        size="small"
                      >
                        View All Threads
                      </Button>
                    </Box>
                  )}
                </ThreadsContainer>
              </StyledCardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </PageLayout>
  );
}
